import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Paper,
  Popover,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/SearchOutlined';
import { useTranslation } from 'react-i18next';
import { type FirestoreError } from 'firebase/firestore';
import { useOutletContext } from 'react-router';
import type { Department, User } from '@altyor-kronos/shared-types';
import { useCollectionSnapshot } from '../../core/data/useCollectionSnapshot';
import { apiGet, ApiError } from '../../core/data/restClient';
import { useAuth } from '../../core/auth/useAuth';
import TimeEntryRow from '../kronos/TimeEntryRow';
import TimeEntryHeader, {
  type ColumnFilterKey,
} from '../kronos/TimeEntryHeader';
import TimeEntryGroupHeader from '../kronos/TimeEntryGroupHeader';
import TimeEntryDetailDrawer from '../kronos/TimeEntryDetailDrawer';
import type { TimeEntry } from '@altyor-kronos/shared-types';
import {
  formatDurationSeconds,
  shouldShowUserColumn,
  sumDurationSeconds,
} from '../kronos/timeEntries';
import {
  useProjectDesignations,
  useProjectEntriesPaged,
} from '../kronos/useKronosData';
import {
  filterEntries,
  filterEntriesByGroup,
  sortEntries,
  type EntrySortDir,
  type EntrySortKey,
} from '../kronos/entryFilter';
import {
  filterByColumn,
  groupEntries,
  type ColumnFilters,
  type EntryGroupKey,
} from '../kronos/entryGroupFilter';
import type { ProjectDetailContext } from './ProjectOverview';
import { colors } from '../../core/theme/theme';
import KronosSummaryPanel from './KronosSummaryPanel';

/**
 * Per-project Kronos tab — `/projects/:id/kronos`.
 *
 * Visibility matrix (mirrors kronos-api `isPrivileged`):
 *   • admin / direction / finance: every teammate's entries
 *   • everyone else, project managers included: only their own rows —
 *     the API re-scopes them to `userId == uid` whatever they ask for
 *
 * Interaction model follows a data-grid-like convention:
 *   • Column headers are clickable → toggle sort on that column
 *     (active column shows an arrow, second click flips direction).
 *   • Filterable columns have an inline filter icon → click opens
 *     a Popover anchored on the icon with the column-specific filter
 *     UI (multi-select for designation/teammate, radio for billable).
 *   • Global search + Group-by stay above the list because they
 *     don't belong to any single column.
 */
export default function ProjectKronosTab() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { project, managers, team } = useOutletContext<ProjectDetailContext>();

  // Scope the query by uid for non-privileged viewers — asking for more
  // than the server grants just gets silently re-scoped, so the client
  // mirrors `shouldShowUserColumn` on both the column AND the query.
  // See KB `firebase/timeentry-uid-scoped-reads.md`.
  const showUser = user ? shouldShowUserColumn(user.roles) : false;
  const userIdScope = showUser || !user ? null : user.uid;
  const { entries, loading, loadingMore, error, loadMore } =
    useProjectEntriesPaged(project.id, userIdScope, 10);

  // Sentinel ref for IntersectionObserver — fires loadMore when the bottom
  // of the list scrolls into view.
  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) loadMore();
      },
      { rootMargin: '120px' },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [loadMore]);

  // Pull designation names once — avoids a flash of raw IDs while the
  // subscription warms up.
  const { designations } = useProjectDesignations(project.id);
  const designationLabelsById = useMemo(() => {
    const m = new Map<string, string>();
    for (const d of designations) m.set(d.id, d.data.name);
    return m;
  }, [designations]);

  const { rows: departmentRows } = useCollectionSnapshot<Department>('departments');
  const departmentsById = useMemo(() => {
    const m = new Map<string, string>();
    for (const d of departmentRows) m.set(d.id, d.data.name);
    return m;
  }, [departmentRows]);

  // For the team view: resolve display names for userIds we don't
  // already have via project managers/members. Rare case (a user
  // might no longer be on the project but still has entries), handled
  // with a batched `documentId in […]` query.
  const userLabelsById = useMemo(() => {
    const m = new Map<string, string>();
    // `team` already includes managers (flattenTeam concatenates every role
    // bucket, and `managers` is the first bucket), but seeding `managers`
    // first keeps the earlier precedence semantics intact and costs nothing.
    for (const u of managers) m.set(u.uid, u.displayName);
    for (const u of team) m.set(u.uid, u.displayName);
    return m;
  }, [managers, team]);

  const [extraUsersFetched, setExtraUsersFetched] = useState<Map<string, string>>(
    () => new Map(),
  );
  const [extraUsersError, setExtraUsersError] = useState<FirestoreError | null>(null);
  // Stable ref tracking which UIDs have already been fetched — avoids the
  // stale-closure problem of reading state inside an effect that omits it
  // from the dependency array.
  const fetchedUidsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!showUser || entries.length === 0) return;
    const missing = new Set<string>();
    for (const e of entries) {
      const uid = e.data.userId;
      if (!userLabelsById.has(uid) && !fetchedUidsRef.current.has(uid)) {
        missing.add(uid);
      }
    }
    if (missing.size === 0) return;
    // Mark as in-flight immediately so concurrent effect runs skip them.
    for (const uid of missing) fetchedUidsRef.current.add(uid);
    const ids = Array.from(missing);
    (async () => {
      try {
        const fetched = new Map<string, string>();
        // Per-doc reads via /api (open to any authenticated user).
        const docs = await Promise.all(
          ids.map((uid) =>
            apiGet<User>(`/users/${uid}`).catch((e) => {
              if (e instanceof ApiError && e.status === 404) return null;
              throw e;
            }),
          ),
        );
        ids.forEach((uid, i) => { const d = docs[i]; if (d) fetched.set(uid, d.displayName ?? uid); });
        setExtraUsersFetched((prev) => {
          const next = new Map(prev);
          for (const [uid, name] of fetched) next.set(uid, name);
          return next;
        });
      } catch (err) {
        setExtraUsersError(err as FirestoreError);
      }
    })();
  }, [showUser, entries, userLabelsById]);

  const resolveUserLabel = (uid: string): string =>
    userLabelsById.get(uid) ?? extraUsersFetched.get(uid) ?? uid;

  // Merge the fetched labels into the single map the pure helpers use.
  const mergedUserLabels = useMemo(() => {
    const m = new Map<string, string>(userLabelsById);
    for (const [uid, name] of extraUsersFetched) m.set(uid, name);
    return m;
  }, [userLabelsById, extraUsersFetched]);

  // uid → group (departmentId). Project members are already loaded; entries
  // by off-project authors resolve to '' (no group) which is fine.
  const userDeptById = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of managers) m.set(u.uid, u.departmentId ?? '');
    for (const u of team) m.set(u.uid, u.departmentId ?? '');
    return m;
  }, [managers, team]);

  // ─── Search + sort + filter + group state ───────────────────────
  const [searchQuery, setSearchQuery] = useState('');
  const [sortKey, setSortKey] = useState<EntrySortKey>('startTime');
  const [sortDir, setSortDir] = useState<EntrySortDir>('desc');
  const [designationFilter, setDesignationFilter] = useState<string[]>([]);
  const [userFilter, setUserFilter] = useState<string[]>([]);
  const [billableFilter, setBillableFilter] = useState<'all' | 'yes' | 'no'>('all');
  const [filterGroup, setFilterGroup] = useState<string>('all');
  const [groupKey, setGroupKey] = useState<EntryGroupKey>('none');

  // Popover anchor for the per-column filter menus. Shared between
  // all three filterable columns — switching column just re-anchors.
  const [filterPopover, setFilterPopover] = useState<{
    key: ColumnFilterKey;
    anchor: HTMLElement;
  } | null>(null);

  const closeFilter = () => setFilterPopover(null);

  // Detail drawer target — null when closed.
  const [detailEntry, setDetailEntry] = useState<TimeEntry | null>(null);

  // Click on a column header: same key → flip direction, new key →
  // switch and default to descending (the most common first glance).
  const handleHeaderSortChange = (key: EntrySortKey) => {
    if (key === sortKey) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  // All entries belong to the same project on this tab, so the label
  // map has one entry — tri par project devient no-op mais reste
  // cohérent avec le header cliquable.
  const projectLabelsById = useMemo(
    () => new Map([[project.id, `${project.code} — ${project.name}`]]),
    [project.id, project.code, project.name],
  );

  // Compose: search → per-column filter → sort → group.
  const columnFilters: ColumnFilters = useMemo(
    () => ({
      designationIds: new Set(designationFilter),
      userIds: new Set(userFilter),
      billable:
        billableFilter === 'yes' ? true : billableFilter === 'no' ? false : null,
    }),
    [designationFilter, userFilter, billableFilter],
  );

  const displayedEntries = useMemo(() => {
    const raw = entries.map((e) => ({ ...e.data, id: e.id }));
    const byGroup = filterEntriesByGroup(raw, filterGroup, userDeptById);
    const bySearch = filterEntries(byGroup, searchQuery, {
      userLabelsById: mergedUserLabels,
      designationLabelsById,
    });
    const byColumn = filterByColumn(bySearch, columnFilters);
    return sortEntries(byColumn, sortKey, sortDir, {
      userLabelsById: mergedUserLabels,
      designationLabelsById,
      projectLabelsById,
    });
  }, [
    entries,
    filterGroup,
    userDeptById,
    searchQuery,
    columnFilters,
    sortKey,
    sortDir,
    mergedUserLabels,
    designationLabelsById,
    projectLabelsById,
  ]);

  const groupedEntries = useMemo(
    () =>
      groupEntries(displayedEntries, groupKey, {
        userLabelsById: mergedUserLabels,
        designationLabelsById,
      }),
    [displayedEntries, groupKey, mergedUserLabels, designationLabelsById],
  );

  // Stable options for the filter popovers — deduped + labelled.
  const designationOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const e of entries) {
      const id = e.data.designationId;
      if (seen.has(id)) continue;
      seen.set(id, designationLabelsById.get(id) ?? id);
    }
    return Array.from(seen, ([id, label]) => ({ id, label })).sort((a, b) =>
      a.label.localeCompare(b.label),
    );
  }, [entries, designationLabelsById]);

  const userOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const e of entries) {
      const uid = e.data.userId;
      if (seen.has(uid)) continue;
      seen.set(uid, mergedUserLabels.get(uid) ?? uid);
    }
    return Array.from(seen, ([uid, label]) => ({ id: uid, label })).sort((a, b) =>
      a.label.localeCompare(b.label),
    );
  }, [entries, mergedUserLabels]);

  const groupOptions = useMemo(() => {
    const ids = new Set<string>();
    for (const e of entries) {
      const g = userDeptById.get(e.data.userId);
      if (g) ids.add(g);
    }
    return [...ids]
      .map((id) => ({ id, label: departmentsById.get(id) ?? id }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [entries, userDeptById, departmentsById]);

  const totalSeconds = sumDurationSeconds(displayedEntries);
  const totalUnfilteredSeconds = sumDurationSeconds(entries.map((e) => e.data));
  const isFiltered =
    searchQuery.trim() !== '' ||
    designationFilter.length > 0 ||
    userFilter.length > 0 ||
    filterGroup !== 'all' ||
    billableFilter !== 'all';

  const activeFilters: Partial<Record<ColumnFilterKey, boolean>> = {
    designation: designationFilter.length > 0,
    teammate: userFilter.length > 0,
    billable: billableFilter !== 'all',
  };

  // Toggle a value in a multi-select filter.
  const toggleDesignation = (id: string) => {
    setDesignationFilter((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };
  const toggleUser = (uid: string) => {
    setUserFilter((prev) =>
      prev.includes(uid) ? prev.filter((x) => x !== uid) : [...prev, uid],
    );
  };

  return (
    <Box data-testid="project-kronos-tab">
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          mb: 2,
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Typography variant="h3">{t('kronos.projectTab.title')}</Typography>
        <Box sx={{ display: 'flex', gap: 3 }}>
          <Typography variant="body2" color="text.secondary">
            {isFiltered
              ? t('kronos.projectTab.entriesFiltered', {
                  shown: displayedEntries.length,
                  total: entries.length,
                })
              : t('kronos.projectTab.entriesShown', { count: entries.length })}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            data-testid="project-kronos-total"
          >
            {t('kronos.projectTab.totalLogged')}:{' '}
            {formatDurationSeconds(isFiltered ? totalSeconds : totalUnfilteredSeconds)}
          </Typography>
        </Box>
      </Box>

      {/* ─── Search (global) + Group-by ─── */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          mb: 2,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <TextField
          size="small"
          placeholder={t('kronos.projectTab.searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ flex: '1 1 240px', maxWidth: 360 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
          data-testid="project-kronos-search"
        />

        <TextField
          select
          size="small"
          label={t('kronos.projectTab.groupBy')}
          value={groupKey}
          onChange={(e) => setGroupKey(e.target.value as EntryGroupKey)}
          sx={{ minWidth: 180 }}
          data-testid="project-kronos-group"
        >
          <MenuItem value="none">{t('kronos.projectTab.groupNone')}</MenuItem>
          <MenuItem value="designation">
            {t('kronos.projectTab.groupDesignation')}
          </MenuItem>
          {showUser && (
            <MenuItem value="teammate">{t('kronos.projectTab.groupTeammate')}</MenuItem>
          )}
          <MenuItem value="day">{t('kronos.projectTab.groupDay')}</MenuItem>
        </TextField>

        <TextField
          select
          size="small"
          label={t('kronos.projectTab.filterGroup')}
          value={filterGroup}
          onChange={(e) => setFilterGroup(e.target.value)}
          sx={{ minWidth: 180 }}
          data-testid="project-kronos-group-filter"
        >
          <MenuItem value="all">{t('kronos.projectTab.filterGroupAll')}</MenuItem>
          {groupOptions.map((g) => (
            <MenuItem key={g.id} value={g.id}>
              {g.label}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {/* ─── Budget overview (Clockify-style status panel) ─── */}
      <KronosSummaryPanel designations={designations} />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} data-testid="project-kronos-error">
          {error.message}
        </Alert>
      )}
      {extraUsersError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {extraUsersError.message}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ borderColor: colors.lightGray, borderRadius: 0 }}>
        {!loading && entries.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }} data-testid="project-kronos-empty">
            <Typography variant="body2" color="text.secondary">
              {showUser
                ? t('kronos.projectTab.empty')
                : t('kronos.projectTab.emptyOwn')}
            </Typography>
          </Box>
        ) : (
          <>
            <TimeEntryHeader
              showUser={showUser}
              showProject={false}
              sortKey={sortKey}
              sortDir={sortDir}
              onSortChange={handleHeaderSortChange}
              onFilterClick={(key, anchor) => setFilterPopover({ key, anchor })}
              activeFilters={activeFilters}
            />
            {displayedEntries.length === 0 ? (
              <Box
                sx={{ p: 4, textAlign: 'center' }}
                data-testid="project-kronos-empty-filtered"
              >
                <Typography variant="body2" color="text.secondary">
                  {t('kronos.projectTab.emptyFiltered')}
                </Typography>
              </Box>
            ) : (
              groupedEntries.map((group) => (
                <Box key={group.key}>
                  {groupKey !== 'none' && (
                    <TimeEntryGroupHeader
                      label={group.label}
                      count={group.entries.length}
                      totalSeconds={group.totalSeconds}
                    />
                  )}
                  {group.entries.map((data) => (
                    <TimeEntryRow
                      key={data.id}
                      entry={data}
                      designationLabel={
                        designationLabelsById.get(data.designationId) ?? data.designationId
                      }
                      userLabel={showUser ? resolveUserLabel(data.userId) : undefined}
                      showUser={showUser}
                      showProject={false}
                      // Editing happens on the tracker page only — no
                      // onEdit/onDelete handlers, so no action buttons.
                      onClick={() => setDetailEntry(data)}
                    />
                  ))}
                </Box>
              ))
            )}

            {/* Sentinel — observed by IntersectionObserver to trigger loadMore */}
            <div ref={sentinelRef} style={{ height: 1 }} aria-hidden="true" />
            {loadingMore && (
              <Box
                sx={{ py: 2, display: 'flex', justifyContent: 'center' }}
                data-testid="project-kronos-loading-more"
              >
                <CircularProgress size={20} thickness={5} color="inherit" />
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* ─── Entry detail drawer ─── */}
      <TimeEntryDetailDrawer
        entry={detailEntry}
        open={detailEntry !== null}
        onClose={() => setDetailEntry(null)}
        projectLabel={`${project.code} — ${project.name}`}
        designationLabel={
          detailEntry
            ? designationLabelsById.get(detailEntry.designationId) ?? detailEntry.designationId
            : undefined
        }
        userLabel={showUser && detailEntry ? resolveUserLabel(detailEntry.userId) : undefined}
      />

      {/* ─── Column filter popovers ─── */}
      <Popover
        open={filterPopover !== null}
        anchorEl={filterPopover?.anchor ?? null}
        onClose={closeFilter}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { borderRadius: 0, minWidth: 240 } } }}
      >
        {filterPopover?.key === 'designation' && (
          <Box sx={{ p: 2 }} data-testid="filter-popover-designation">
            <Typography variant="caption" sx={{ letterSpacing: 0.8 }}>
              {t('kronos.projectTab.filterDesignation').toUpperCase()}
            </Typography>
            <Stack sx={{ mt: 1, maxHeight: 320, overflowY: 'auto' }}>
              {designationOptions.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t('kronos.projectTab.filterNoOption')}
                </Typography>
              ) : (
                designationOptions.map((o) => (
                  <FormControlLabel
                    key={o.id}
                    control={
                      <Checkbox
                        size="small"
                        checked={designationFilter.includes(o.id)}
                        onChange={() => toggleDesignation(o.id)}
                      />
                    }
                    label={<Typography variant="body2">{o.label}</Typography>}
                  />
                ))
              )}
            </Stack>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Button
                size="small"
                onClick={() => setDesignationFilter([])}
                disabled={designationFilter.length === 0}
              >
                {t('kronos.projectTab.filterClear')}
              </Button>
              <Button size="small" variant="contained" onClick={closeFilter}>
                {t('common.confirm')}
              </Button>
            </Box>
          </Box>
        )}

        {filterPopover?.key === 'teammate' && (
          <Box sx={{ p: 2 }} data-testid="filter-popover-teammate">
            <Typography variant="caption" sx={{ letterSpacing: 0.8 }}>
              {t('kronos.projectTab.filterTeammate').toUpperCase()}
            </Typography>
            <Stack sx={{ mt: 1, maxHeight: 320, overflowY: 'auto' }}>
              {userOptions.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t('kronos.projectTab.filterNoOption')}
                </Typography>
              ) : (
                userOptions.map((o) => (
                  <FormControlLabel
                    key={o.id}
                    control={
                      <Checkbox
                        size="small"
                        checked={userFilter.includes(o.id)}
                        onChange={() => toggleUser(o.id)}
                      />
                    }
                    label={<Typography variant="body2">{o.label}</Typography>}
                  />
                ))
              )}
            </Stack>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Button
                size="small"
                onClick={() => setUserFilter([])}
                disabled={userFilter.length === 0}
              >
                {t('kronos.projectTab.filterClear')}
              </Button>
              <Button size="small" variant="contained" onClick={closeFilter}>
                {t('common.confirm')}
              </Button>
            </Box>
          </Box>
        )}

        {filterPopover?.key === 'billable' && (
          <Box sx={{ p: 2 }} data-testid="filter-popover-billable">
            <Typography variant="caption" sx={{ letterSpacing: 0.8 }}>
              {t('kronos.projectTab.filterBillable').toUpperCase()}
            </Typography>
            <RadioGroup
              value={billableFilter}
              onChange={(_, v) => setBillableFilter(v as 'all' | 'yes' | 'no')}
              sx={{ mt: 1 }}
            >
              <FormControlLabel
                value="all"
                control={<Radio size="small" />}
                label={
                  <Typography variant="body2">
                    {t('kronos.projectTab.filterBillableAll')}
                  </Typography>
                }
              />
              <FormControlLabel
                value="yes"
                control={<Radio size="small" />}
                label={
                  <Typography variant="body2">
                    {t('kronos.projectTab.filterBillableYes')}
                  </Typography>
                }
              />
              <FormControlLabel
                value="no"
                control={<Radio size="small" />}
                label={
                  <Typography variant="body2">
                    {t('kronos.projectTab.filterBillableNo')}
                  </Typography>
                }
              />
            </RadioGroup>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
              <Button size="small" variant="contained" onClick={closeFilter}>
                {t('common.confirm')}
              </Button>
            </Box>
          </Box>
        )}
      </Popover>
    </Box>
  );
}
